'use client';
import React, {
  useEffect,
  useContext,
  PropsWithChildren,
  createContext,
  useRef,
  // useState,
} from 'react';
// import { webapi } from '@spot-fe/utils';
import { isBrowser, saLogin, uniRequest } from '@spot-fe/utils';
import { createStore, useStore } from 'zustand';
import { API2_HOST } from '@bfe/by-dynamic-domain';

export interface IUserInfo {
  id: number;
  username: string;
  vague_email: string;
  vague_mobile: string;
  mobile_verified: boolean;
  email_verified: boolean;
  has_google2fa: boolean;
  country_code: string;
  currency_code: string;
  avatar: string;
  lang: string;
  status: number;
  is_submember: boolean;
  unified_account: number;
  loyalty_vip_info: {
    is_vip: boolean;
    label_level: number;
    label_type: number;
  };
  member_tag: {
    UTA: string;
    change_utc_time_zone: string;
  };
  grayFlag: {
    IsUTA: boolean;
    IsFA: boolean;
  };
  created_at: string;
  sub_attribute: number;
}
export interface UserInfoProps {
  userInfo?: IUserInfo;
  isUTA: boolean;
  isLogin: boolean;
  isFetchLoginFinally: boolean;
  setUserInfo?: (userInfo: IUserInfo) => void;
  setFetchLoginFinally?: (isFetchLoginFinally: boolean) => void;
  isFA: boolean;
  isSub: boolean;
  isUnified: boolean;
}

export type UserInfoState = UserInfoProps;

export type UserAction = {
  setUserInfo: (userInfo: IUserInfo) => Promise<void>;
  setFetchLoginFinally: (isFetchLoginFinally: boolean) => void;
};

export type UserInfoStore = ReturnType<typeof createUserInfoStore>;

export const UserInfoContext = createContext<UserInfoStore | null>(null);

const DEFAULT_PROPS: UserInfoProps = {
  userInfo: undefined,
  isUTA: false,
  isLogin: false,
  isFetchLoginFinally: false,
  isFA: false,
  isSub: false,
  isUnified: false,
};

export const createUserInfoStore = (initProps?: Partial<UserInfoProps>) => {
  return createStore<UserInfoState & UserAction>()((set) => ({
    ...DEFAULT_PROPS,
    ...initProps,
    setUserInfo: async (userInfo: IUserInfo) => {
      if (isBrowser) {
        const sa = window?.sensors;
        (window as any).GA_UID = userInfo?.id;
        sa?.registerPage({
          // eslint-disable-next-line camelcase
          is_ominibus_acc: userInfo?.member_tag?.UTA === 'SUCCESS',
        });
        sa?.quick('autoTrack');
        saLogin(userInfo?.id);
      }
      return set((state) => ({
        ...state,
        userInfo,
        isUTA: userInfo?.member_tag?.UTA === 'SUCCESS', // 否是统一账户
        isLogin: userInfo?.id > 0, // 是否登录
        isFA: userInfo?.grayFlag?.IsFA, // 是否资金用户
        isSub: userInfo?.is_submember, // 是否子账号
        isUnified: userInfo?.unified_account === 2, // 是否升级为统保账户
        isFetchLoginFinally: true
      }));
    },
    setFetchLoginFinally: (isFetchLoginFinally: boolean) =>
      set((state) => ({
        ...state,
        isFetchLoginFinally,
      })),
  }));
};

export const getUserInfo = async () => {
  const baseUrl = process.env.NODE_ENV === 'development' ? process.env.BYBIT_API2_HOST : API2_HOST
  const res: any = await uniRequest.get(`${baseUrl}/v2/private/user/profile`, {
    useCache: true,
  });
  const { data } = res;
  return data;
};

UserInfoContext.displayName = 'UserInfoContext';

export const UserInfoProvider = ({ children }: PropsWithChildren) => {
  // const [isAppSupportJsBridge, setAppSupportJsBridge] = useState(true);
  const store = useRef(createUserInfoStore({})).current;
  const setUserInfo = useStore(store, (s) => s.setUserInfo);
  const setFetchLoginFinallyStore = useStore(
    store,
    (s) => s.setFetchLoginFinally
  );

  const fetchUserInfoWeb = async (cb?: (r: number | undefined) => void) => {
    try {
      const response = await getUserInfo();
      setUserInfo?.(response?.result);
      cb?.(response?.result?.id);
    } catch (e) {
      cb?.(undefined);
      console.log('getWebUserInfo err', e);
      setFetchLoginFinallyStore?.(true);
    }
  };

  const fetchUserInfo = (cb?: (r: number | undefined) => void) => {
    // if (isApp() && useJsBridgeInApp && isAppSupportJsBridge) {
    //   fetchUserInfoApp();
    // } else {
    //   fetchUserInfoWeb(cb);
    // }
    fetchUserInfoWeb(cb);
  };

  // useEffect(() => {
  //   getIsAppSupportJsBridge({
  //     successCallback: (res) => {
  //       const isAppSupportJsBridge = res?.params?.isAppSupportJsBridge;
  //       setAppSupportJsBridge(isAppSupportJsBridge);
  //     },
  //   });
  // }, []);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  return (
    <UserInfoContext.Provider value={store}>
      {children}
    </UserInfoContext.Provider>
  );
};

export function useUserInfoContext<T>(
  selector: (state: UserInfoState) => T,
  equalityFn?: (left: T, right: T) => boolean
): T {
  const store = useContext(UserInfoContext);
  if (!store) throw new Error('Missing UserInfoContext.Provider in the tree');
  console.log('store', store);
  return useStore(store, selector, equalityFn);
}

export const useUserInfo = () => {
  const store = useContext(UserInfoContext);
  if (!store) throw new Error('Missing UserInfoContext.Provider in the tree');
  const info = useStore(store, (s) => {
    return ({
      ...s,
      userInfo: s.userInfo,
      isUTA: s.isUTA,
      isLogin: s.isLogin,
      isFA: s.isFA,
      isSub: s.isSub,
      isUnified: s.isUnified,
      isKYC: s && s.userInfo && s.userInfo["kyc_person_level"] > 0,
    })
  });
  return info;
};

export const useSetUserInfo = () => {
  const store = useContext(UserInfoContext);
  if (!store) throw new Error('Missing UserInfoContext.Provider in the tree');
  const setUserInfo = useStore(store, (s) => s.setUserInfo);
  return setUserInfo;
};

export default UserInfoContext;
